const initCollectionNavigationReveal = () => {
  if (!('IntersectionObserver' in window)) {
    return;
  }

  const cards = document.querySelectorAll('.cnav-card[reveal-on-scroll="true"]');
  if (!cards.length) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          return;
        }

        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.15 }
  );

  cards.forEach((card) => {
    observer.observe(card);
  });
};

const setActiveCard = (cards, activeCard) => {
  cards.forEach((card) => {
    card.classList.toggle('is-active', card === activeCard);
  });
};

const applyProductFilter = (handle, categoryHandle = handle) => {
  const products = document.querySelectorAll('.cnav-filterable-product');
  const seenProductHandles = new Set();

  products.forEach((product) => {
    product.classList.remove('cnav-striped-duplicate');

    // Leftover from the old "show striped copies as a second color" pass. Must never linger or
    // it'll keep displacing that product inside its own collection's grid and corrupt the inline
    // banner's configurable "after Nth product" placement.
    const card = product.querySelector('.pcard');
    if (card) card.style.order = '';

    // Inline banners (see onuia_collection-single-filter.liquid) share this same wrapper/data
    // attribute as products, but should only ever show while one specific category is filtered
    // in — never in the unfiltered "all categories" view.
    const isBanner = product.querySelector('.collection-inline-banner') !== null;

    if (!handle) {
      if (isBanner) {
        product.hidden = true;
        return;
      }

      // A product that belongs to more than one combined collection renders once per collection
      // (see onuia_collection-single-filter.liquid) so per-category filtering still works — but
      // with no category selected, only the first copy of each product should actually show.
      // Never keep a striped copy visible as a second color: the color filter is always on, so
      // the first copy already represents that product in the active color (or is hidden if it
      // doesn't offer it).
      const productHandle = product.querySelector('.pcard')?.getAttribute('data-handle');
      const isDuplicate = Boolean(productHandle) && seenProductHandles.has(productHandle);
      if (productHandle) seenProductHandles.add(productHandle);

      product.hidden = isDuplicate;
      return;
    }

    const categories = (product.getAttribute('data-categories') || '').trim().split(/\s+/);
    product.hidden = !categories.includes(handle);
  });

  // Let onuia_collection-single-filter.liquid's own script know the visible product
  // set changed, so it can hide color swatches not offered by the active category and
  // re-apply the current color filter (never more than one color on the grid).
  document.dispatchEvent(new CustomEvent('cnav:filter-changed', {
    detail: {
      handle,
      collectionHandle: handle,
      categoryHandle: categoryHandle || handle,
    },
  }));
};

const getDefaultCard = (cards) => {
  const prioritized = Array.from(cards).find(
    (card) => card.getAttribute('data-prioritize-preselection') === 'true'
  );

  return prioritized || cards[0];
};

const updateCollectionParam = (handle) => {
  if (!window.history) {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  if (handle) {
    params.set('collection', handle);
  } else {
    params.delete('collection');
  }

  const query = params.toString();
  history.pushState({}, '', `${window.location.pathname}${query ? '?' + query : ''}`);
};

const initCollectionNavigationFilter = () => {
  document.querySelectorAll('.cnav-track').forEach((track) => {
    const allowDeselect = track.getAttribute('data-allow-category-deselect') !== 'false';
    const cards = track.querySelectorAll('.cnav-card[data-collection-handle]');
    if (!cards.length) {
      return;
    }

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        const isAlreadyActive = card.classList.contains('is-active');
        if (isAlreadyActive && !allowDeselect) {
          return;
        }

        const nextCollectionHandle = isAlreadyActive ? null : card.getAttribute('data-collection-handle');
        const nextCategoryHandle = isAlreadyActive
          ? null
          : card.getAttribute('data-category-handle') || nextCollectionHandle;

        setActiveCard(cards, isAlreadyActive ? null : card);
        applyProductFilter(nextCollectionHandle, nextCategoryHandle);
        updateCollectionParam(nextCollectionHandle);
      });
    });

    const requestedHandle = new URLSearchParams(window.location.search).get('collection');
    let matchingCard = requestedHandle
      ? Array.from(cards).find((card) => card.getAttribute('data-collection-handle') === requestedHandle)
      : null;

    if (!matchingCard) {
      const defaultCard = getDefaultCard(cards);
      const hasPrioritized = defaultCard?.getAttribute('data-prioritize-preselection') === 'true';

      if (!allowDeselect || hasPrioritized) {
        matchingCard = defaultCard;
      }
    }

    const activeHandle = matchingCard?.getAttribute('data-collection-handle') ?? null;
    const activeCategoryHandle = matchingCard?.getAttribute('data-category-handle') || activeHandle;

    if (matchingCard) {
      setActiveCard(cards, matchingCard);
    }

    if (activeHandle && !requestedHandle) {
      updateCollectionParam(activeHandle);
    }

    // Always run once on load — dedupes products shared across combined collections
    // (see applyProductFilter), not just after a card click.
    applyProductFilter(activeHandle, activeCategoryHandle);
  });
};

const initCollectionNavigationTrackFades = () => {
  document.querySelectorAll('.cnav-track-wrap').forEach((wrap) => {
    const track = wrap.querySelector('.cnav-track');
    if (!track) return;

    const updateFades = () => {
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      const left = track.scrollLeft;
      wrap.classList.toggle('is-overflow-start', left > 2);
      wrap.classList.toggle('is-overflow-end', left < max - 2);
    };

    track.addEventListener('scroll', updateFades, { passive: true });
    window.addEventListener('resize', updateFades);
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', updateFades, { once: true });
    });
    updateFades();
  });
};

initCollectionNavigationReveal();
initCollectionNavigationFilter();
initCollectionNavigationTrackFades();

const initCollectionNavigationArrows = () => {
  document.querySelectorAll('[data-cnav-arrows]').forEach((slider) => {
    const track = slider.querySelector('.cnav-track');
    const prev = slider.querySelector('[data-cnav-prev]');
    const next = slider.querySelector('[data-cnav-next]');
    if (!track || !prev || !next) return;

    const cards = Array.from(track.querySelectorAll('.cnav-card'));
    if (cards.length < 3) return;

    let page = 0;
    const oddCount = cards.length % 2 === 1;
    const lastPage = Math.ceil(cards.length / 2) - 1;

    const cardLeft = (card) => {
      const trackRect = track.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      return cardRect.left - trackRect.left + track.scrollLeft;
    };

    const maxScroll = () => Math.max(0, track.scrollWidth - track.clientWidth);

    const scrollToPage = (nextPage) => {
      page = Math.max(0, Math.min(nextPage, lastPage));
      const atEnd = oddCount && page === lastPage;
      const left = atEnd ? maxScroll() : cardLeft(cards[page * 2]);
      track.scrollTo({ left, behavior: 'smooth' });
      prev.disabled = page <= 0;
      next.disabled = page >= lastPage;
    };

    const align = () => {
      const card = track.querySelector('.cnav-card');
      if (!card) return;
      const sliderRect = slider.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const center = cardRect.top - sliderRect.top + cardRect.height / 2;
      prev.style.top = center + 'px';
      next.style.top = center + 'px';
    };

    let scrollTimer = 0;

    const syncPageFromScroll = () => {
      const left = track.scrollLeft;
      const end = maxScroll();
      if (oddCount && left >= end - 2) {
        page = lastPage;
      } else {
        let closest = 0;
        let best = Infinity;
        for (let index = 0; index < cards.length; index += 2) {
          const distance = Math.abs(cardLeft(cards[index]) - left);
          if (distance < best) {
            best = distance;
            closest = index / 2;
          }
        }
        page = closest;
      }
      prev.disabled = page <= 0;
      next.disabled = page >= lastPage;
    };

    const update = () => {
      align();
      syncPageFromScroll();
    };

    prev.addEventListener('click', () => {
      scrollToPage(page - 1);
    });

    next.addEventListener('click', () => {
      scrollToPage(page + 1);
    });

    track.addEventListener('scroll', () => {
      align();
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(syncPageFromScroll, 80);
    }, { passive: true });
    window.addEventListener('resize', update);
    track.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', update, { once: true });
    });
    update();
  });
};

initCollectionNavigationArrows();
